import { PropsWithChildren } from "react";
import {
  backgroundColorClassName,
  flexJustifyClassName,
  textFontClassName,
  transitionClassName,
} from "./commonClassNames";
import { ChildrenOnlyProp, ColorProps, FlexJustifyProp } from "./commonProps";

type CardColorProps = Pick<ColorProps, "primary">;

type CardWideProp = {
  wide?: boolean;
  className?: string;
  style?: React.CSSProperties;
};

export type CardProps = React.HTMLAttributes<HTMLDivElement> & CardWideProp & CardColorProps & { className?: string };

export type CardHeaderProps = CardWideProp;

export type CardRowProps = FlexJustifyProp & { classes?: string };

export const Card: React.FC<CardProps> = ({ children, primary, wide, className, style, ...props }) => {
  const combinedClassName = [
    "flex flex-col justify-start shadow rounded-md h-fit",
    wide ? "w-full p-1 max-w-7xl" : " gap-2 p-1 w-72 md:justify-items-center grow",
    backgroundColorClassName({ primary }),
    transitionClassName({ colors: true }),
    className ?? "",
  ].join(" ");

  return (
    <div {...props} style={style} className={combinedClassName}>
      {children}
    </div>
  );
};

export const CardHeader: React.FC<PropsWithChildren<CardHeaderProps>> = ({ children, wide, className }) => {
  const combinedClassName = [
    "flex flex-wrap items-center  min-w-96",
    "py-1 text-left text-ellipsis",
    "text-primary-200 dark:text-white bg-neutral-100 ",
    wide ? "sm:justify-between justify-center gap-4 pr-8 pl-7 py-8" : "justify-between gap-2 px-4 py-4 ",
    textFontClassName({
      lineHeight: 11,
      font: "semibold",
      size: "xl",
      uppercase: true,
    }),
    transitionClassName({ colors: true }),
    className ?? "",
  ].join(" ");

  return <div className={combinedClassName}>{children}</div>;
};

export const CardRow: React.FC<PropsWithChildren<CardRowProps>> = ({ children, justify = "between", className }) => {
  const combinedClassName = [
    "flex flex-row grow items-center overflow-auto",
    flexJustifyClassName({ justify }),
    className ?? "",
  ].join(" ");

  return <div className={combinedClassName}>{children}</div>;
};

export const CardSection: React.FC<ChildrenOnlyProp> = ({ children, className }) => {
  const combinedClassName = [
    "h-fit w-full flex flex-col gap-1 rounded-md pb-3 pt-0 px-3",
    "bg-neutral-100 dark:bg-neutral-700",
    className ?? "",
  ].join(" ");
  return <div className={combinedClassName}>{children}</div>;
};

export const CardSectionCompact: React.FC<ChildrenOnlyProp> = ({ children }) => {
  const className = ["h-fit w-full flex flex-col gap-1 rounded-md p-3", " dark:bg-neutral-700"].join(" ");
  return <div className={className}>{children}</div>;
};
